import { Suspense } from 'react';
import {
  Table,
  ContentfulComponentHeaderToptasks,
  ContentfulComponentLogoBar,
  ContentfulComponentProductCards,
} from '~lazy-components';
import { ComponentHero } from '~components/ComponentHero';
import { ComponentTextMedia } from '~components/ComponentTextMedia';
import { ComponentTextWithToc } from '~components/ComponentTextWithToc';
import { ComponentReviews } from '~components/ComponentReviews';
import { ComponentActivityCards } from '~components/ComponentActivityCards';
import { ComponentCoverageList } from '~components/ComponentCoverageList';
import { ComponentProductTiles } from '~components/ComponentProductTiles';
import { ComponentUspCarousel } from '~components/ComponentUspCarousel';
import { ComponentImageTextGroup } from '~components/ComponentImageTextGroup';

import ContentfulComponentCustom from '~components/ContentfulComponentCustom';
// import ContentfulComponentHeaderToptasks from '~components/ContentfulComponentHeaderToptasks';
// import ContentfulComponentLogoBar from '~components/ContentfulComponentLogoBar';
// import ContentfulComponentProductCards from '~components/ContentfulComponentProductCards';
import ContentfulComponentRelatedArticles from '~components/ContentfulComponentRelatedArticles';
import { ContentfulComponentCardGroup } from '~components/ContentfulComponentCardGroup';
import { ComponentCoverageLandingHeader } from '~components/ComponentCoverageLandingHeader';
import Text from '../Text';
import { ComponentSharedCollapsibleList } from '~components/ComponentSharedCollapsibleList';
import { ComponentCoverageLandingBody } from '~components/ComponentCoverageLandingBody';
import CoverageLandingImageText from '../CoverageLandingImageText';
import { getContentBlockPaddings } from './getContentBlockPaddings';

import type { ContentfulComponentCoverageLandingBody } from '~components/ComponentCoverageLandingBody';
import type { ContentfulComponentSharedCollapsibleList } from '~components/ComponentSharedCollapsibleList';
import type { ContentfulComponentCoverageLandingHeader } from '~components/ComponentCoverageLandingHeader';
import type { ContentfulComponentSharedText } from '../Text';
import type { ContentfulComponentHeaderToptasksContent } from '~components/ContentfulComponentHeaderToptasks';
import type { ContentfulComponentSharedImageText } from '~components/ComponentSharedImageText';
import type { ContentfulComponentImageTextGroup } from '~components/ComponentImageTextGroup';
import type { ContentfulComponentLogoBarContent } from '~components/ContentfulComponentLogoBar';
import type { ContentfulComponentProductCardsContent } from '~components/ContentfulComponentProductCards';
import type { ContentfulComponentRelatedArticlesContent } from '~components/ContentfulComponentRelatedArticles';
import type { ContentfulComponentTableContent } from '~components/Table';
import type { ContentfulComponentCardGroupContent } from '~components/ContentfulComponentCardGroup';
import type { ContentfulComponentCustomContent } from '~components/ContentfulComponentCustom';
import type { ContentfulComponentHero } from '~components/ComponentHero';
import type { ContentfulComponentTextMedia } from '~components/ComponentTextMedia';
import type { ContentfulComponentTextWithToc } from '~components/ComponentTextWithToc';
import type { ContentfulComponentReviews } from '~components/ComponentReviews';
import type { ContentfulComponentActivityCards } from '~components/ComponentActivityCards';
import type { ContentfulComponentCoverageList } from '~components/ComponentCoverageList';
import type { ContentfulComponent, ContentBlock } from '~components/types';
import type { ContentfulComponentProductTiles } from '~components/ComponentProductTiles';
import type { ContentfulComponentUspCarousel } from '~components/ComponentUspCarousel';
import type { Layout, Theme } from '~models/contentful';

export type ContentBlockType = ContentfulComponent &
  (
    | ContentfulComponentHero
    | ContentfulComponentTextMedia
    | ContentfulComponentTextWithToc
    | ContentfulComponentReviews
    | ContentfulComponentActivityCards
    | ContentfulComponentCoverageList
    | ContentfulComponentCoverageLandingBody
    | ContentfulComponentCoverageLandingHeader
    | ContentfulComponentSharedCollapsibleList
    | ContentfulComponentImageTextGroup
    | ContentfulComponentSharedImageText
    | ContentfulComponentSharedText
    | ContentfulComponentHeaderToptasksContent
    | ContentfulComponentLogoBarContent
    | ContentfulComponentProductCardsContent
    | ContentfulComponentRelatedArticlesContent
    | ContentfulComponentTableContent
    | ContentfulComponentCardGroupContent
    | ContentfulComponentCustomContent
    | ContentfulComponentProductTiles
    | ContentfulComponentUspCarousel
  );

const ContentBlockRenderer: ContentBlock<{ content: ContentBlockType }> = ({ content, ...sectionProps }) => {
  switch (content.__typename) {
    case 'ContentfulComponentHero':
      return <ComponentHero content={content} {...sectionProps} />;
    case 'ContentfulComponentTextMedia':
      return <ComponentTextMedia content={content} {...sectionProps} />;
    case 'ContentfulComponentTextWithToc':
      return <ComponentTextWithToc content={content} {...sectionProps} />;
    case 'ContentfulComponentReviews':
      return <ComponentReviews content={content} {...sectionProps} />;
    case 'ContentfulComponentActivityCards':
      return <ComponentActivityCards content={content} {...sectionProps} />;
    case 'ContentfulComponentCoverageList':
      return <ComponentCoverageList content={content} {...sectionProps} />;
    case 'ContentfulComponentCoverageLandingHeader':
      return <ComponentCoverageLandingHeader content={content} {...sectionProps} />;
    case 'ContentfulComponentCoverageLandingBody':
      return <ComponentCoverageLandingBody content={content} {...sectionProps} />;
    case 'ContentfulComponentSharedCollapsibleList':
      return <ComponentSharedCollapsibleList content={content} {...sectionProps} />;
    case 'ContentfulComponentSharedImageText':
      return <CoverageLandingImageText content={content} {...sectionProps} />;
    case 'ContentfulComponentSharedText':
      return <Text content={content} {...sectionProps} />;
    case 'ContentfulComponentImageTextGroup':
      return <ComponentImageTextGroup content={content} {...sectionProps} />;
    case 'ContentfulComponentHeaderToptasks':
      return <ContentfulComponentHeaderToptasks content={content} {...sectionProps} />;
    case 'ContentfulComponentLogoBar':
      return <ContentfulComponentLogoBar content={content} {...sectionProps} />;
    case 'ContentfulComponentProductCards':
      return <ContentfulComponentProductCards content={content} {...sectionProps} />;
    case 'ContentfulComponentRelatedArticles':
      return <ContentfulComponentRelatedArticles content={content} {...sectionProps} />;
    case 'ContentfulComponentCardGroup':
      return <ContentfulComponentCardGroup content={content} {...sectionProps} />;
    case 'ContentfulComponentProductTiles':
      return <ComponentProductTiles content={content} {...sectionProps} />;
    case 'ContentfulComponentUspCarousel':
      return <ComponentUspCarousel content={content} {...sectionProps} />;
    case 'ContentfulComponentTable':
      return (
        <Suspense>
          <Table content={content} {...sectionProps} />
        </Suspense>
      );
    case 'ContentfulComponentCustom':
      return <ContentfulComponentCustom content={content} {...sectionProps} />;
    default:
      return null;
  }
};

/**
 * Default theme and padding multiplier for components. The defined theme is used if no theme is available from
 * Contentful and padding multiplier overrides the default padding sizes for both small and large paddings.
 */
const defaults: Partial<Record<ContentBlockType['__typename'], { theme?: Theme; paddingMultiplier?: number }>> = {
  // ContentfulComponentCallToActionsBar: { theme: 'protectivepurple-50', paddingMultiplier: 0.5 },
  // ContentfulComponentCoverageLandingOtherCoverages: { theme: 'protectivepurple-50', paddingMultiplier: 0.5 },
};

const getContentBlockThemeAndLayout = (block: ContentBlockType) => {
  let theme: Theme | undefined;
  let blockLayout: Layout;

  /* If a component's theme equals null it means no theme has been set in Contentful but the component requires to have a theme.
  In such cases we default to a pre-defined value for the component or (if that doesn't exist) to 'light'.
  The theme can also be undefined; that means the component doesn't support themes and nothing will be applied. */
  if (block.theme === null) theme = defaults[block.__typename]?.theme || 'neutral-white';
  else theme = block.theme;

  if (
    block.__typename === 'ContentfulComponentTextWithToc' ||
    block.__typename === 'ContentfulComponentActivityCards' ||
    block.__typename === 'ContentfulComponentUspCarousel' ||
    block.__typename === 'ContentfulComponentProductTiles' ||
    block.__typename === 'ContentfulComponentImageTextGroup'
  ) {
    theme = 'neutral-white';
  }

  if (block.__typename === 'ContentfulComponentCustom') {
    if (block.component === 'fr-disability-gap-simulator') theme = 'light';
    if (block.component === 'lever-job-postings') theme = 'putty-50';
  }

  /* If the component has a light theme set, it doesn't make sense to use it together with the boxed layout,
  because that would result in a white box inside a white container with extra padding.
  Therefore the full-width layout is force-applied in this case. */
  if (theme === 'light' || theme === 'neutral-white') blockLayout = 'full-width';
  else blockLayout = block.blockLayout || 'full-width';

  return { theme, blockLayout };
};

export const ContentBlocks: React.FC<{ contentBlocks: ContentBlockType[] }> = ({ contentBlocks }): JSX.Element[] => {
  /* 1. First we need to 'normalize' the content blocks by taking care of missing themes/layouts,
  applying generic or component-specific fallbacks and overriding theme-layout combinations that don't make sense. */
  const normalizedContentBlocks = contentBlocks.map((block) => ({ ...block, ...getContentBlockThemeAndLayout(block) }));

  /* 2. Then we calculate the paddings based on the adjusted content blocks. */
  const paddings = getContentBlockPaddings(normalizedContentBlocks);

  return normalizedContentBlocks.map((block, index) => {
    const trackingKey: string = index + (block.name ?? block.title ?? block.contentfulid ?? block.__typename);

    return (
      <ContentBlockRenderer
        style={
          {
            '--padding-multiplier': defaults[block.__typename]?.paddingMultiplier,
          } as React.CSSProperties
        }
        key={trackingKey}
        trackingId={trackingKey}
        content={block}
        theme={block.theme}
        blockLayout={block.blockLayout}
        verticalPadding={paddings[index]}
        imageLoadingStrategy={index === 0 ? 'eager' : 'lazy'}
        imageFetchPriority={index === 0 ? 'high' : 'low'}
        headingBaseLevel={index === 0 ? 1 : 2}
      />
    );
  });
};
